import React, { useState, useEffect, useMemo } from 'react';
import { multiply, divide, add } from 'mathjs';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  useGridApiRef,
  GridRowModes,
} from '@mui/x-data-grid-premium';
import FactoryIcon from '@mui/icons-material/Factory';
import { Button } from '@mui/material';

import { dataGridPremiumColumns } from '../../utils/helper';
import { ConfirmDialogUI, DataGridIdentifiers, DatagridPremiumUi, InputUI } from '../Interface';
import prodgeneratorService from '../../service/prodgenerator.service';
import useUsers from '../../context/UsersContext';
import useConfig from '../../context/ConfigContext';
import { useLoader } from '../../provider/LoaderProvider';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const ProductionGeneratorNewProdTable = ({
  removedItemState,
  refetchState,
  selectedItemsState,
  selectedRowsState,
  treeDataState,
  sourceDataState,
}) => {
  const apiRef = useGridApiRef();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { showLoader, hideLoader } = useLoader();

  const { removedItem, setRemovedItem } = removedItemState;
  const { setRefetchTable } = refetchState;
  const { selectedItems, setSelectedItems } = selectedItemsState;
  const { setSelectedRows } = selectedRowsState;
  const { setTreeData } = treeDataState;
  const { setSourceData } = sourceDataState;

  const [newProds, setNewProds] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
  }, []);

  const clearEditRows = () => {
    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);
    for (const rowId of rowsInEditMode) {
      apiRef.current.stopRowEditMode({ id: rowId });
    }
  };

  useEffect(() => {
    if (removedItem) {
      setNewProds((prev) =>
        prev.filter((prod) => {
          return (
            prod.baseDocType !== removedItem.docTypeId ||
            prod.baseId !== removedItem.docId ||
            prod.baseLine !== removedItem.lineNum
          );
        })
      );

      clearEditRows();
    }
  }, [removedItem]);

  const createRows = async () => {
    if (!selectedItems.length) return;
    showLoader();

    const itemCodes = selectedItems.map((item) => item.itemCode);
    const allSourceData = await prodgeneratorService.getAllSourceDataByItemCodes(itemCodes);

    for (const addedItem of selectedItems) {
      const sourceObj = allSourceData.find((data) => {
        if (data?.sourceData?.[0]?.rootItemCode === addedItem.itemCode) return data;
        else return false;
      });

      const sourceData = sourceObj?.sourceData || [];

      if (
        !addedItem?.qty ||
        !sourceData?.length ||
        sourceData?.[0]?.rootItemCode !== addedItem.itemCode ||
        newProds.some((np) => np.baseId === addedItem.docId && np.baseLine === addedItem.lineNum)
      )
        continue;

      const generatedProds = [];

      for (const source of sourceData) {
        const inProd = generatedProds.find(
          (prod) => prod.level === source.level - 1 && prod.articleNumber === source.parentItemCode
        );

        const prodQty =
          source.level === 1
            ? addedItem.doc_joker_num2 !== null
              ? parseFloat(addedItem.doc_joker_num2)
              : parseFloat(addedItem.qty)
            : multiply(
                inProd?.productionQuantity ?? 1,
                divide(source.neededQuantity ?? 1, source.rootBaseQuantity ?? 1)
              );

        const reference = [addedItem.reference, addedItem.itemReference].filter(Boolean).join(' - ');

        const sd = {
          baseId: addedItem.docId,
          baseLine: addedItem.lineNum,
          baseDocNum: addedItem.docNum,
          baseDocType: addedItem.docTypeId,
          companyId: addedItem.companyId,
          endDate: addedItem.dueDate,
          articleNumber: source.relatedItemCode,
          joker_int1: source.joker_int1,
          joker_int2: source.joker_int2,
          joker_int3: source.joker_int3,
          joker_int4: source.joker_int4,
          joker_int5: source.joker_int5,
          joker_int6: source.joker_int6,
          joker_int7: source.joker_int7,
          joker_char1: source.joker_char1,
          joker_char2: source.joker_char2,
          joker_char3: source.joker_char3,
          joker_char4: source.joker_char4,
          joker_char5: source.joker_char5,
          joker_char6: source.joker_char6,
          joker_char7: source.joker_char7,
          joker_char8: source.joker_char8,
          joker_char9: source.joker_char9,
          joker_char10: source.joker_char10,
          joker_char11: source.joker_char11,
          joker_char12: source.joker_char12,
          joker_char13: source.joker_char13,
          joker_char14: source.joker_char14,
          joker_char15: source.joker_char15,
          joker_char16: source.joker_char16,
          joker_char17: source.joker_char17,
          joker_char18: source.joker_char18,
          joker_char19: source.joker_char19,
          joker_char20: source.joker_char20,
          joker_char21: source.joker_char21,
          joker_char22: source.joker_char22,
          joker_char23: source.joker_char23,
          joker_char24: source.joker_char24,
          joker_char25: source.joker_char25,
          joker_num1: source.joker_num1,
          joker_num2: source.joker_num2,
          joker_num3: source.joker_num3,
          joker_num4: source.joker_num4,
          joker_num5: source.joker_num5,
          articleName: source.relatedItemName,
          productionQuantity: prodQty,
          projectId: addedItem.projectId,
          me: source.reletedQtyUnitId,
          description: addedItem.internalNote,
          reference,
          finishedItemCode: source.rootItemCode,
          level: source.level,
          productionOperation:
            source.sourceList?.sourceListOperation?.map((o) => {
              const baseQty = multiply(o.sourceListOperationMachine?.[0].neededQuantity, prodQty);
              return {
                operationId: o.operationId,
                quantityUnitId: o.sourceListOperationMachine[0].quantityUnitId,
                resourceOneId: o.sourceListOperationMachine?.[0].resourceId,
                baseRate: o.sourceListOperationMachine?.[0].neededQuantity,
                visOrder: o.visOrder,
                description: o.description,
                fixedLeadTime: o.fixedLeadTime,
                baseQty: baseQty,
                plannedQty: add(baseQty, o.fixedLeadTime ?? 0),
                dependence: o.dependence,
              };
            }) ?? [],
          productionItem:
            source.sourceList?.sourceListItem?.map((i) => ({
              itemCode: i.itemCode,
              quantity: multiply(i.neededQuantity, prodQty),
              baseRate: i.neededQuantity,
              joker_char1: i.joker_char1,
              joker_num1: i.joker_num1,
              joker_num2: i.joker_num2,
              description: i.description,
              issueMethod: i.issueMethod,
              whsCode: i.whsCode,
              secondaryProduct: i.secondaryProduct,
            })) ?? [],
        };

        generatedProds.push(sd);
      }

      setNewProds((prev) => [...prev, ...generatedProds]);
      setTreeData(allSourceData?.[allSourceData.length - 1]?.treeView ?? []);
      setSourceData(allSourceData?.[allSourceData.length - 1]?.sourceData);
      clearEditRows();
      hideLoader();
    }
  };

  useEffect(() => {
    createRows();
  }, [selectedItems]);

  const handleProdQtyChange = (newValue, row) => {
    const newQty = parseFloat(newValue) || 0;
    setNewProds((prev) => {
      const index = prev.findIndex(
        (prod) =>
          prod.baseId === row.baseId &&
          prod.baseLine === row.baseLine &&
          prod.baseDocType === row.baseDocType &&
          prod.articleNumber === row.articleNumber
      );

      if (index === -1) return prev;

      const updatedProd = {
        ...prev[index],
        productionQuantity: newQty,
        productionOperation: prev[index].productionOperation?.map((o) => ({
          ...o,
          plannedQty: multiply(o.baseRate, newQty),
        })),
        productionItem: prev[index].productionItem?.map((i) => ({
          ...i,
          quantity: multiply(i.baseRate, newQty),
        })),
      };

      const updatedProds = [...prev];
      updatedProds[index] = updatedProd;

      return updatedProds;
    });
  };

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'baseDocNum', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (Object.keys(user).length) {
      setLoading(false);
    }
  }, [user]);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'baseDocNum',
        headerName: 'Vere',
        type: 'string',
      },
      {
        field: 'baseLine',
        headerName: 'Sor',
        type: 'string',
      },
      {
        field: 'finishedItemCode',
        headerName: 'Késztermék',
        type: 'string',
      },
      {
        field: 'articleNumber',
        headerName: 'Cikk',
        type: 'string',
      },
      {
        field: 'articleName',
        headerName: 'Megnevezés',
        type: 'string',
      },
      {
        field: 'productionQuantity',
        headerName: 'Mennyiség',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="productionQuantity"
              type="number"
              variant="standard"
              fullWidth
              inputProps={{ min: 0.01, step: 'any' }}
              value={data.row.productionQuantity}
              onChange={(e) => {
                const val = e.target.value;
                const newValue = val === '' ? '' : parseFloat(val);

                if (val === '' || (newValue >= 0 && !isNaN(newValue))) {
                  handleProdQtyChange(newValue, data.row);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newValue,
                  });
                }
              }}
            />
          );
        },
      },
      /*{
        //@TODO
        field: 'me',
        headerName: 'ME',
        type: 'string',
      },*/
      {
        field: 'level',
        headerName: 'Szint',
        type: 'string',
      },
      {
        field: 'projectId',
        headerName: 'Projekt',
        type: 'string',
      },

      {
        field: 'description',
        headerName: 'Leírás',
        type: 'string',
      },
      {
        field: 'reference',
        headerName: 'Referencia',
        type: 'string',
      },
    ]);
  }, []);

  const CustomToolbar = ({ newProds, setShowConfirmDialog }) => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <Button
          size="small"
          sx={{ ml: 'auto' }}
          startIcon={<FactoryIcon />}
          onClick={() => {
            setShowConfirmDialog(true);
          }}
          disabled={newProds.length === 0}
        >
          Generálás
        </Button>
      </GridToolbarContainer>
    );
  };

  const handleGenClick = () => {
    showLoader();
    addNotification({
      content: 'A gyártások generálása elkezdődött!',
      type: NotificationType.INFO,
    });

    prodgeneratorService
      .createProduction(newProds)
      .then((data) => {
        setNewProds([]);
        setRemovedItem(null);
        addNotification({
          content: 'A gyártások generálása sikeres!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setSelectedItems([]);
        setSelectedRows([]);
      })
      .finally(() => {
        hideLoader();
      });
  };

  return (
    <>
      {!loading && (
        <DatagridPremiumUi
          apiRef={apiRef}
          datagridIdentifier={DataGridIdentifiers.ProductionGeneratorNewProdTable}
          datagridInitialState={initialState}
          height={400}
          rows={newProds}
          columns={[...columns]}
          paginationAndfilterMode="client"
          getRowId={(row) => `${row.baseId}_${row.baseLine}_${row.baseDocType}_${row.articleNumber}`}
          disableRowSelectionOnClick
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              newProds,
              setShowConfirmDialog,
            },
          }}
          editMode="row"
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellClick={(params, event) => {
            const { row } = params;
            const generatedRowId = `${row.baseId}_${row.baseLine}_${row.baseDocType}_${row.articleNumber}`;

            const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

            for (const rowId of rowsInEditMode) {
              if (generatedRowId !== rowId) {
                apiRef.current.stopRowEditMode({ id: rowId });
              }
            }
            const rowMode = apiRef.current.getRowMode(generatedRowId);
            if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: generatedRowId });
          }}
        />
      )}

      <ConfirmDialogUI
        handleChangeElement={() => {
          handleGenClick();
          setShowConfirmDialog(false);
        }}
        show={showConfirmDialog}
        onHide={() => {
          setShowConfirmDialog(false);
        }}
        message={`Elkészíti a ${newProds.length} gyártási utasítást?`}
      />
    </>
  );
};

export default ProductionGeneratorNewProdTable;
